import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { LoginRequest, RegisterRequest, AuthResponse } from '@/types'

import type { User } from '@/types'

interface LoginApiResponse {
  success: boolean
  message: string
  data?: {
    user: User
    token: string
    token_type: string
    expires_in: number
  }
}

/** Подтянуть параметры эффективной подписки и слить в объект пользователя (отдельный GET от `/profile`). */
export async function mergeSubscriptionParameters(user: User): Promise<User> {
  try {
    const params = await authApi.getSubscriptionParameters()
    return { ...user, subscription_parameters: params }
  } catch {
    return { ...user, subscription_parameters: user.subscription_parameters ?? {} }
  }
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<LoginApiResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    )
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Ошибка авторизации')
    }

    return {
      user: response.data.data.user,
      token: response.data.data.token,
      token_type: response.data.data.token_type,
      expires_in: response.data.data.expires_in,
    }
  },

  refresh: async (): Promise<{ token: string; expires_in: number }> => {
    interface RefreshApiResponse {
      success: boolean
      message: string
      data?: {
        token: string
        expires_in: number
      }
    }

    const response = await apiClient.post<RefreshApiResponse>(API_ENDPOINTS.AUTH.REFRESH)

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Ошибка обновления токена')
    }

    return {
      token: response.data.data.token,
      expires_in: response.data.data.expires_in,
    }
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<LoginApiResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      data
    )
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Ошибка регистрации')
    }

    return {
      user: response.data.data.user,
      token: response.data.data.token,
      token_type: response.data.data.token_type,
      expires_in: response.data.data.expires_in,
    }
  },

  logout: async (): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT)
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email })
  },

  getSubscriptionParameters: async (): Promise<Record<string, string>> => {
    interface SubscriptionParametersResponse {
      success?: boolean
      data?: Record<string, string>
    }
    const response = await apiClient.get<SubscriptionParametersResponse>(
      API_ENDPOINTS.AUTH.SUBSCRIPTION_PARAMETERS,
    )
    if (response.data?.data && typeof response.data.data === 'object') {
      return response.data.data
    }
    return {}
  },


  getProfile: async (): Promise<User> => {
    interface ProfileResponse {
      data?: User
      id?: number
      message?: string
    }
    const response = await apiClient.get<ProfileResponse>(API_ENDPOINTS.AUTH.PROFILE)
    
    if (response.data?.data) {
      return response.data.data
    }

    if (response.data && response.data.id) {
      return response.data as User
    }

    throw new Error(response.data?.message || 'Ошибка загрузки профиля')
  },
}
